import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { BellDropdown } from "@/components/BellDropdown";
import { SignOut, House, User, ShieldStar, Star, Trophy, UsersThree, Gift, Storefront, ChatCircleDots, GraduationCap } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export function AgentLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const canChat = isAdmin || (user?.agent_status || "active") === "active";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to={isAdmin ? "/admin" : "/dashboard"} data-testid="header-logo-link">
            <Logo size="default" />
          </Link>

          <div className="flex items-center gap-4 sm:gap-5">
            {isAdmin ? (
              <Link
                to="/admin"
                className={`hidden sm:flex items-center gap-2 text-sm transition-colors ${
                  location.pathname.startsWith("/admin") ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
                }`}
                data-testid="nav-admin-link"
              >
                <ShieldStar size={18} weight="duotone" /> Admin
              </Link>
            ) : (
              <Link
                to="/dashboard"
                className={`hidden sm:flex items-center gap-2 text-sm transition-colors ${
                  location.pathname === "/dashboard" ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
                }`}
                data-testid="nav-dashboard-link"
              >
                <House size={18} weight="duotone" /> Dashboard
              </Link>
            )}

            <Link
              to="/clients"
              className={`hidden sm:flex items-center gap-2 text-sm transition-colors ${
                location.pathname === "/clients" ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
              }`}
              data-testid="nav-clients-link"
            >
              <UsersThree size={18} weight="duotone" /> Clients
            </Link>

            <Link
              to="/leaderboard"
              className={`hidden sm:flex items-center gap-2 text-sm transition-colors ${
                location.pathname === "/leaderboard" ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
              }`}
              data-testid="nav-leaderboard-link"
            >
              <Trophy size={18} weight="duotone" /> Leaderboard
            </Link>

            <Link
              to="/suppliers"
              className={`hidden md:flex items-center gap-2 text-sm transition-colors ${
                location.pathname === "/suppliers" ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
              }`}
              data-testid="nav-suppliers-link"
            >
              <Storefront size={18} weight="duotone" /> Suppliers
            </Link>

            {canChat && (
              <Link
                to="/chat"
                className={`hidden md:flex items-center gap-2 text-sm transition-colors ${
                  location.pathname === "/chat" ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
                }`}
                data-testid="nav-chat-link"
              >
                <ChatCircleDots size={18} weight="duotone" /> Chat
              </Link>
            )}

            <Link
              to="/training"
              className={`hidden md:flex items-center gap-2 text-sm transition-colors ${
                location.pathname === "/training" ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
              }`}
              data-testid="nav-training-link"
            >
              <GraduationCap size={18} weight="duotone" /> Training
            </Link>

            {!isAdmin && (
              <Link
                to="/referrals"
                className={`hidden md:flex items-center gap-2 text-sm transition-colors ${
                  location.pathname === "/referrals" ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
                }`}
                data-testid="nav-referrals-link"
              >
                <Gift size={18} weight="duotone" /> Referrals
              </Link>
            )}

            <Link
              to="/reviews"
              target="_blank"
              className="hidden md:flex items-center gap-2 text-sm text-white/70 hover:text-[#D4AF37] transition-colors"
              data-testid="nav-reviews-link"
            >
              <Star size={18} weight="duotone" /> Reviews
            </Link>

            <Link
              to="/profile"
              className={`hidden md:flex items-center gap-2 text-sm transition-colors ${
                location.pathname === "/profile" ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
              }`}
              data-testid="nav-profile-link"
            >
              <User size={18} weight="duotone" />
              <span data-testid="nav-user-name">{user?.name || user?.email}</span>
              {isAdmin && (
                <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-1.5 py-0.5 rounded-full">
                  Admin
                </span>
              )}
            </Link>

            <BellDropdown />

            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="nav-logout-btn"
            >
              <SignOut size={18} weight="duotone" />
              <span className="hidden sm:inline ml-1">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">{children}</main>

      <footer className="border-t border-white/5 mt-20 py-8 text-center text-xs text-white/40 font-sans">
        © {new Date().getFullYear()} Best Time Travel & Tours · Curated luxury journeys
      </footer>
    </div>
  );
}
